import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams, Navigate } from 'react-router-dom'
import { ProtectedLessonMaster } from '@/components/auth/ProtectedLessonMaster'
import { PlayerInterface } from '@/components/player/PlayerInterface'
import { useLessonAuth } from '@/hooks/useLessonAuth'
import { useAuthStore } from '@/store/authStore'
import { apiClient } from '@/services/api'
import { useTerminology } from '@/hooks/useTerminology'
import type { Lesson } from '@/types'

export function LessonSession() {
  const { lessonId } = useParams()
  const [searchParams] = useSearchParams()
  const [lesson, setLesson] = useState<Lesson | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isAuthenticated } = useAuthStore()
  const { canHost, canPlay } = useLessonAuth(lesson)
  const t = useTerminology()

  const requestedRole = searchParams.get('role') || 'player'
  const isHostRequested = searchParams.get('host') === 'true' || requestedRole === 'host'

  useEffect(() => {
    if (!lessonId) return

    const loadLesson = async () => {
      try {
        setLoading(true)
        setError(null)

        // Get lesson information (authenticated endpoint for hosts)
        const response = await apiClient.getLesson(lessonId)

        if (response.success && response.data?.lesson) {
          setLesson(response.data.lesson)
        } else {
          throw new Error(response.message || 'Lesson not found')
        }
      } catch (error: any) {
        setError(error.message || 'Failed to load lesson')
      } finally {
        setLoading(false)
      }
    }

    loadLesson()
  }, [lessonId])

  // Handle invalid lesson ID
  if (!lessonId) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">?</div>
          <h1 className="text-2xl font-bold mb-4">{`Invalid ${t.lesson.title} ID`}</h1>
          <p className="text-gray-400">{`Please check the ${t.lesson.lower} ID and try again.`}</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{`Loading ${t.lesson.lower} session...`}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">!</div>
          <h1 className="text-2xl font-bold mb-4">{`${t.lesson.title} Not Found`}</h1>
          <p className="text-gray-400 mb-4">
            {error || `The ${t.lesson.lower} you're looking for doesn't exist or has been removed.`}
          </p>
          <p className="text-sm text-gray-500 mb-6">{`${t.lesson.title} ID: ${lessonId}`}</p>
          <div className="space-y-2">
            <button
              onClick={() => window.history.back()}
              className="ds-btn ds-btn-primary mr-2"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/dashboard'}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Determine if user should see LessonMaster interface
  const shouldShowLessonMaster = isHostRequested && isAuthenticated && canHost

  // If host is requested but user can't host, redirect to player interface
  if (isHostRequested && (!isAuthenticated || !canHost)) {
    // Redirect to player interface for this lesson
    return <Navigate to={`/lesson/${lessonId}?role=player`} replace />
  }

  return shouldShowLessonMaster ?
    <ProtectedLessonMaster lesson={lesson} /> :
    <PlayerInterface />
}
